import {useEffect, useState} from 'react';

import {view} from '@sail/ui';
import {animate, view as animatedView, AnimatePresence} from '@sail/engine';

import {Input} from '../Chat/Input';
import {InputProps} from '../Chat/InputProps';

const PLACEHOLDER_TRANSITION_DURATION_MS = 300;
const PLACEHOLDER_BUFFER_MS = 4000;

export const InputWithRotatingPlaceholders: React.FC<
  Omit<InputProps, 'placeholder'> & {placeholders: string[]}
> = ({placeholders, ...props}) => {
  const [rotatingIndex, setRotatingIndex] = useState(0);
  // this isVisible is specific to mounting/unmounting the placeholder
  // text that <AnimatePresence> relies on to animate on entry/exit
  const [isVisible, setIsVisible] = useState(true);
  // unfortunately we need this state value to hide the animate
  // presense if a user types, so we immediately get rid of the
  // "psuedo placeholder" text... otherwise it animates on exit which
  // leads to the animated text and user-typed text temporarily overlapping
  const [isInputEmpty, setIsInputEmpty] = useState(true);
  const currentPlaceholder = placeholders[rotatingIndex] || '';

  useEffect(() => {
    if (!placeholders || placeholders.length === 0) return;
    if (!isInputEmpty) return; // pause while input is not empty..

    const intervalIds: Array<ReturnType<typeof setInterval>> = [];
    const timeoutIds: Array<ReturnType<typeof setTimeout>> = [];

    const intervalId = setInterval(() => {
      // "hide" current placeholder by removing from react tree, which
      // the animate presence will catch unmount and animate on exit
      setIsVisible(false);
      // After exit animation completes, update index to force remount
      const transitionTimeoutId = setTimeout(() => {
        setRotatingIndex((prev) => (prev + 1) % placeholders.length);

        // Small delay to ensure React processes the index change before showing
        // this is a hack to ensure the animation is triggered on entry
        const visibilityTimeoutId = setTimeout(() => setIsVisible(true), 50);
        timeoutIds.push(visibilityTimeoutId);
      }, PLACEHOLDER_TRANSITION_DURATION_MS);
      timeoutIds.push(transitionTimeoutId);
    }, PLACEHOLDER_BUFFER_MS);

    intervalIds.push(intervalId);

    return () => {
      intervalIds.forEach((id) => clearInterval(id));
      timeoutIds.forEach((id) => clearTimeout(id));
    };
  }, [placeholders, isInputEmpty]);

  return (
    <view.div css={{maxHeight: 'fit-content'}}>
      <view.div css={{position: 'relative'}}>
        {isInputEmpty ? (
          <AnimatePresence>
            {isVisible ? (
              <animatedView.span
                key={currentPlaceholder}
                uses={[
                  animate.in({
                    duration: PLACEHOLDER_TRANSITION_DURATION_MS,
                    opacity: 0,
                    transform: 'translateY(5px)',
                    easing: 'ease-out',
                  }),
                  animate.out({
                    duration: PLACEHOLDER_TRANSITION_DURATION_MS,
                    opacity: 0,
                    transform: 'translateY(-10px)',
                    easing: 'ease-in',
                  }),
                ]}
                css={{
                  zIndex: 1,
                  left: '2.25rem',
                  top: '0.7rem',
                  position: 'absolute',
                  lineHeight: '1.5rem',
                  pointerEvents: 'none',
                  touchAction: 'none',
                  paddingRight: '3rem',
                  textColor: 'subdued',
                }}
              >
                {currentPlaceholder}
              </animatedView.span>
            ) : null}
          </AnimatePresence>
        ) : null}
      </view.div>
      <Input
        {...props}
        aria-label={currentPlaceholder}
        onInputChanged={(v) => setIsInputEmpty(v.length === 0)}
      />
    </view.div>
  );
};
